import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import type { AttendanceRow } from "./attendance";

// ---- Types ----

export type SessionState = "working" | "paused" | "ended";

export interface SessionRow {
  id: string;
  userId: string;
  workUnitId: string;
  workUnitName: string | null;
  workUnitCtrlNo: string | null;
  workUnitClientName: string | null;
  workUnitProjectName: string | null;
  attendanceId: string | null;
  state: SessionState;
  pauseReason: "manual" | "auto_logout_no_markout" | "network_down" | null;
  startedAt: string;
  endedAt: string | null;
  totalPausedMs: number;
  elapsedMs: number;
  lastHeartbeatAt: string | null;
}

export interface MeSessionState {
  today: string;
  attendance: AttendanceRow | null;
  session: SessionRow | null;
}

export interface PendingWorkUnitRow {
  workUnitId: string;
  ctrlNo: string;
  projectName: string | null;
  clientName: string | null;
  currentWorkflowName: string | null;
  stepsCompleted: number;
  totalSteps: number;
  lastWorkedAt: string | null;
  dueAt: string | null;
}

// ---- Keys ----

const meStateKey = ["me", "session-state"] as const;
const pendingWUsKey = ["me", "pending-work-units"] as const;
const pendingWUsCountKey = ["me", "pending-work-units", "count"] as const;
const liveStatusKey = ["admin", "live-status"] as const;

// ---- Hooks ----

export function useMeSessionState(enabled = true) {
  return useQuery<MeSessionState>({
    queryKey: meStateKey,
    queryFn: () => api.get<MeSessionState>("/me/session-state").then((r) => r.data),
    staleTime: 5_000,
    enabled,
  });
}

export function usePendingWorkUnits(enabled = true, search: string = "") {
  return useQuery<PendingWorkUnitRow[]>({
    queryKey: [...pendingWUsKey, { search }],
    queryFn: () =>
      api
        .get<PendingWorkUnitRow[]>("/me/pending-work-units", {
          params: { search: search || undefined },
        })
        .then((r) => r.data),
    enabled,
  });
}

export function usePendingWorkUnitsCount(enabled = true) {
  return useQuery<{ count: number }>({
    queryKey: pendingWUsCountKey,
    queryFn: () =>
      api.get<{ count: number }>("/me/pending-work-units/count").then((r) => r.data),
    enabled,
  });
}

export function useStartSession() {
  const qc = useQueryClient();
  return useMutation<SessionRow, unknown, { workUnitId: string }>({
    mutationFn: (input) => api.post<SessionRow>("/sessions", input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: meStateKey }),
  });
}

export function usePauseSession() {
  const qc = useQueryClient();
  return useMutation<SessionRow, unknown, { sessionId: string; reason?: "manual" | "network_down" }>({
    mutationFn: ({ sessionId, reason = "manual" }) =>
      api.post<SessionRow>(`/sessions/${sessionId}/pause`, { reason }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: meStateKey }),
  });
}

export function useResumeSession() {
  const qc = useQueryClient();
  return useMutation<SessionRow, unknown, { sessionId: string }>({
    mutationFn: ({ sessionId }) =>
      api.post<SessionRow>(`/sessions/${sessionId}/resume`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: meStateKey }),
  });
}

export function useStopSession() {
  const qc = useQueryClient();
  return useMutation<SessionRow, unknown, { sessionId: string }>({
    mutationFn: ({ sessionId }) =>
      api.post<SessionRow>(`/sessions/${sessionId}/stop`, {}).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: meStateKey }),
  });
}

export function useSwitchSession() {
  const qc = useQueryClient();
  return useMutation<SessionRow, unknown, { sessionId: string; newWorkUnitId: string }>({
    mutationFn: ({ sessionId, newWorkUnitId }) =>
      api.post<SessionRow>(`/sessions/${sessionId}/switch`, { newWorkUnitId }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: meStateKey }),
  });
}

export function useHeartbeat() {
  return useMutation<{ sessionId: string; elapsedMs: number; at: string }, unknown, { sessionId: string; elapsedMs: number }>({
    mutationFn: ({ sessionId, elapsedMs }) =>
      api.patch(`/sessions/${sessionId}/heartbeat`, { elapsedMs }).then((r) => r.data),
  });
}

// ---- Admin live status (Module 12) ----

export type EffectiveStatus = "working" | "paused" | "idle" | "completed" | "offline";

export interface LiveStatusRow {
  userId: string;
  fullName: string;
  email: string;
  role: string;
  isMarkedIn: boolean;
  markedInAt: string | null;
  markedOutAt: string | null;
  sessionState: SessionState | null;
  currentWorkUnit: {
    id: string;
    ctrlNo: string;
    name: string;
    clientName: string | null;
    projectName: string | null;
  } | null;
  todayActiveSeconds: number;
  todayPausedSeconds: number;
  lastActivityAt: string | null;
  effectiveStatus: EffectiveStatus;
  pendingWorkUnitsCount: number;
}

export interface LiveStatusResponse {
  employees: LiveStatusRow[];
  summary: {
    total: number;
    working: number;
    paused: number;
    idle: number;
    completed: number;
    offline: number;
  };
  serverTime: string;
}

export function useLiveStatus(enabled = true, refetchMs = 5_000) {
  return useQuery<LiveStatusResponse>({
    queryKey: liveStatusKey,
    queryFn: () => api.get<LiveStatusResponse>("/admin/live-status").then((r) => r.data),
    refetchInterval: enabled ? refetchMs : false,
    enabled,
  });
}

// ---- Per-user detailed report (drilldown) ----

export interface DetailedReportSession {
  id: string;
  startedAt: string;
  endedAt: string | null;
  activeSeconds: number;
  pausedSeconds: number;
  state: SessionState;
}

export interface DetailedReportWorkUnit {
  workUnitId: string;
  ctrlNo: string | null;
  projectName: string | null;
  clientName: string | null;
  totalActiveSeconds: number;
  totalPausedSeconds: number;
  stepsCompleted: number;
  totalSteps: number;
  isCompleted: boolean;
  sessions: DetailedReportSession[];
}

export interface DetailedReportDay {
  date: string;
  status: string;
  isWeekend: boolean;
  markInTime: string | null;
  markOutTime: string | null;
  totalSeconds: number;
  regularised: boolean;
  leaveType: string | null;
}

export interface DetailedReportDailyBreakdown {
  date: string;
  workUnitId: string | null;
  activeSeconds: number;
  pausedSeconds: number;
}

export interface DetailedReportResponse {
  user: {
    id: string;
    fullName: string;
    email: string;
    role: string;
  };
  range: { start: string; end: string };
  attendance: {
    days: DetailedReportDay[];
    summary: {
      totalWorkingDays: number;
      daysPresent: number;
      daysActual: number;
      daysRegularised: number;
      daysAbsent: number;
      daysOnLeave: number;
      daysWeekend: number;
      daysHalfDay: number;
    };
  };
  workUnits: DetailedReportWorkUnit[];
  dailyBreakdown: DetailedReportDailyBreakdown[];
  totals: {
    totalActiveSeconds: number;
    totalPausedSeconds: number;
  };
}

export function useUserDetailedReport(
  userId: string | undefined,
  params: { startDate?: string; endDate?: string } = {},
  opts: { enabled?: boolean } = {},
) {
  return useQuery<DetailedReportResponse>({
    queryKey: ["reports", "user-detailed", userId, params.startDate, params.endDate],
    queryFn: () =>
      api
        .get<DetailedReportResponse>(`/reports/users/${userId}/detailed`, {
          params: {
            startDate: params.startDate,
            endDate: params.endDate,
          },
        })
        .then((r) => r.data),
    enabled: !!userId && (opts.enabled ?? true),
    staleTime: 30_000,
  });
}

// Re-export AttendanceRow so existing imports `from "@/api/hooks/timetracking"` keep working.
export type { AttendanceRow };
